import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  MapPin,
  Package,
  History,
  BarChart3,
  ClipboardCheck,
  Upload,
  ArrowLeftRight,
  ScrollText
} from 'lucide-react';
import './Navbar.css';

const Navbar = ({ user, onLogout, adminNav, managerNav, isSidebarCollapsed, onSidebarCollapse, onOpenSupport, onOpenDashboardHelpVideo }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const collapsed = !!isSidebarCollapsed;

  const getRoleDisplay = (role) => {
    const roleMap = {
      power_user: 'Power User',
      admin: 'Admin',
      manager: 'Manager',
      site: 'Site'
    };
    return roleMap[role] || role;
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    return name
      .split(/\s+/)
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <motion.aside
      className={`navbar navbar-design ${collapsed ? 'navbar-collapsed' : ''}`}
      role="navigation"
      initial={false}
      animate={{ width: collapsed ? 80 : 256 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className={`navbar-brand ${collapsed ? 'navbar-brand-collapsed' : ''}`}>
        <img
          src={`${process.env.PUBLIC_URL || ''}/logo.png`}
          alt=""
          className="navbar-logo"
          onError={(e) => { e.target.style.display = 'none'; }}
          aria-hidden
        />
        {!collapsed && <h1>SimplyInventory</h1>}
      </div>

      {adminNav && (
        <nav className="navbar-admin-nav">
          <div className="navbar-admin-nav-item-wrap">
            <button
              type="button"
              className={`navbar-admin-nav-item ${adminNav.activeTab === 'users' ? 'active' : ''}`}
              onClick={() => adminNav.setActiveTab('users')}
              title={collapsed ? 'Users' : undefined}
            >
              <Users size={20} />
              <span>Users</span>
            </button>
            {adminNav.importNotifications?.users && (
              <button
                type="button"
                className="import-notification-bubble"
                onClick={(e) => { e.stopPropagation(); adminNav.onOpenNotification?.('users'); }}
                aria-label="View import notification"
                title="Import notification"
              />
            )}
          </div>
          <div className="navbar-admin-nav-item-wrap">
            <button
              type="button"
              className={`navbar-admin-nav-item ${adminNav.activeTab === 'sites' ? 'active' : ''}`}
              onClick={() => adminNav.setActiveTab('sites')}
              title={collapsed ? 'Sites' : undefined}
            >
              <MapPin size={20} />
              <span>Sites</span>
            </button>
            {adminNav.importNotifications?.sites && (
              <button
                type="button"
                className="import-notification-bubble"
                onClick={(e) => { e.stopPropagation(); adminNav.onOpenNotification?.('sites'); }}
                aria-label="View import notification"
                title="Import notification"
              />
            )}
          </div>
          <div className="navbar-admin-nav-item-wrap">
            <button
              type="button"
              className={`navbar-admin-nav-item ${adminNav.activeTab === 'inventory' ? 'active' : ''}`}
              onClick={() => adminNav.setActiveTab('inventory')}
              title={collapsed ? 'Inventory' : undefined}
            >
              <Package size={20} />
              <span>Inventory</span>
            </button>
            {adminNav.importNotifications?.inventory && (
              <button
                type="button"
                className="import-notification-bubble"
                onClick={(e) => { e.stopPropagation(); adminNav.onOpenNotification?.('inventory'); }}
                aria-label="View import notification"
                title="Import notification"
              />
            )}
          </div>
          <button
            type="button"
            className={`navbar-admin-nav-item ${adminNav.activeTab === 'archived' ? 'active' : ''}`}
            onClick={() => adminNav.setActiveTab('archived')}
            title={collapsed ? 'Archived Counts' : undefined}
          >
            <History size={20} />
            <span>Archived Counts</span>
          </button>
          <button
            type="button"
            className={`navbar-admin-nav-item ${adminNav.activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => adminNav.setActiveTab('reports')}
            title={collapsed ? 'Reports' : undefined}
          >
            <BarChart3 size={20} />
            <span>Reports</span>
          </button>
          <button
            type="button"
            className={`navbar-admin-nav-item ${adminNav.activeTab === 'import' ? 'active' : ''}`}
            onClick={() => adminNav.setActiveTab('import')}
            title={collapsed ? 'Import' : undefined}
          >
            <Upload size={20} />
            <span>Import</span>
          </button>
          {user?.role === 'admin' && (
            <button
              type="button"
              className={`navbar-admin-nav-item ${adminNav.activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => adminNav.setActiveTab('audit')}
              title={collapsed ? 'Audit' : undefined}
            >
              <ScrollText size={20} />
              <span>Audit</span>
            </button>
          )}
        </nav>
      )}

      {managerNav && (
        <nav className="navbar-admin-nav">
          <button
            type="button"
            className={`navbar-admin-nav-item ${managerNav.activeTab === 'count-review' ? 'active' : ''}`}
            onClick={() => managerNav.setActiveTab('count-review')}
            title={collapsed ? 'Count Review' : undefined}
          >
            <ClipboardCheck size={20} />
            <span>Count Review</span>
          </button>
          <button
            type="button"
            className={`navbar-admin-nav-item ${managerNav.activeTab === 'transfers' ? 'active' : ''}`}
            onClick={() => managerNav.setActiveTab('transfers')}
            title={collapsed ? 'Transfers' : undefined}
          >
            <ArrowLeftRight size={20} />
            <span>Transfers</span>
          </button>
          <button
            type="button"
            className={`navbar-admin-nav-item ${managerNav.activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => managerNav.setActiveTab('reports')}
            title={collapsed ? 'Reports' : undefined}
          >
            <BarChart3 size={20} />
            <span>Reports</span>
          </button>
        </nav>
      )}

      <div className={`navbar-user ${collapsed ? 'navbar-user-collapsed' : ''}`}>
        <div className="navbar-profile-card">
          <div className="navbar-avatar-wrap">
            <div className="navbar-avatar">{getInitials(user?.name)}</div>
            <span className="navbar-avatar-status" aria-hidden="true" />
          </div>
          <div className="navbar-profile-info">
            <span className="navbar-profile-name">{user?.name || 'User'}</span>
            <span className="navbar-profile-role">{getRoleDisplay(user?.role)}</span>
          </div>
          <div className="navbar-profile-actions" ref={menuRef}>
            <button
              type="button"
              className="navbar-ellipsis"
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Profile options"
              aria-expanded={showMenu}
            >
              &#8230;
            </button>
            {showMenu && (
              <div className="navbar-dropdown">
                {onOpenDashboardHelpVideo && (
                  <button type="button" onClick={() => { setShowMenu(false); onOpenDashboardHelpVideo(); }}>
                    Dashboard guide
                  </button>
                )}
                <button type="button" onClick={() => { setShowMenu(false); onOpenSupport?.(); }}>
                  Support
                </button>
                <button type="button" onClick={() => { setShowMenu(false); onLogout?.(); }}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.aside>
  );
};

export default Navbar;
